import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, LogOut, Leaf, Loader2 } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { formatINR } from '../lib/plans';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusTone = (s) => (s === 'paid' ? 'bg-brand-forest text-brand-bg' : 'bg-brand-gold/30 text-brand-ink');
const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-IN', { dateStyle: 'medium' }); } catch { return iso; }
};

export default function GardenPage() {
  const { user, loading, login, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = React.useState(null);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (loading) return;
    if (!user) {
      setOrders([]);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/garden`, { credentials: 'same-origin' });
        if (!res.ok) throw new Error('load');
        const data = await res.json();
        setOrders(data.orders || []);
      } catch {
        setError('Could not load your garden. Please try again.');
      }
    })();
  }, [user, loading]);

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  if (loading || (user && orders === null)) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-brand-forest" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center px-6" data-testid="garden-signed-out">
        <div className="max-w-md text-center">
          <div className="inline-flex items-center gap-2 text-brand-gold text-xs tracking-[0.25em] uppercase mb-4">
            <Leaf className="w-3.5 h-3.5" /> Your garden
          </div>
          <h1 className="font-serif text-4xl text-brand-ink font-light leading-tight mb-4">
            Sign in to see <em className="text-brand-forest">every tree</em> you've gifted.
          </h1>
          <p className="text-brand-ink2 leading-relaxed mb-8">
            One click with Google — no passwords to remember. We match your existing
            dedications by email.
          </p>
          <button
            onClick={login}
            data-testid="garden-google-login"
            className="rounded-full bg-brand-forest text-brand-bg px-8 py-3.5 text-sm tracking-wide font-medium hover:bg-brand-ink transition-all inline-flex items-center gap-2"
          >
            Continue with Google →
          </button>
          <div className="mt-6">
            <Link to="/" className="inline-flex items-center gap-2 text-brand-ink2 text-sm hover:text-brand-forest">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to site
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="garden-page">
      <div className="sticky top-0 z-20 bg-brand-bg border-b border-brand-line">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-brand-ink2 hover:text-brand-forest">
            <ArrowLeft className="w-4 h-4" /> Back to Pallavit
          </Link>
          <div className="flex items-center gap-3">
            {user.picture && (
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-brand-line object-cover" />
            )}
            <span className="text-sm text-brand-ink hidden sm:block">{user.name}</span>
            <button onClick={handleLogout} data-testid="garden-logout-button" className="rounded-full border border-brand-line hover:bg-brand-bg2 px-3 py-1.5 text-xs text-brand-ink2 inline-flex items-center gap-1.5">
              <LogOut className="w-3 h-3" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-10 py-14">
        <p className="text-brand-gold tracking-[0.25em] uppercase text-xs mb-5 font-medium">Your garden</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-brand-ink leading-[1.05] tracking-tight font-light mb-3">
          Welcome back, <em className="text-brand-forest">{user.name}.</em>
        </h1>
        <p className="text-brand-ink2 text-lg mb-10">
          {orders.length === 0
            ? "You haven't planted a tree yet. Your first dedication will live here forever."
            : `${orders.length} ${orders.length === 1 ? 'dedication' : 'dedications'}, tended by our team in Nashik.`}
        </p>

        {error && <div className="mb-6 text-red-700 text-sm">{error}</div>}

        {orders.length === 0 ? (
          <Link to="/" className="inline-flex rounded-full bg-brand-forest text-brand-bg px-8 py-3.5 text-sm tracking-wide font-medium hover:bg-brand-ink transition-all">
            Gift your first tree →
          </Link>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5" data-testid="garden-orders">
            {orders.map((o) => (
              <div key={o.order_id} className="rounded-2xl border border-brand-line bg-brand-bg2 p-6" data-testid={`garden-order-${o.order_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-brand-gold tracking-[0.2em] uppercase text-[10px] mb-1">{fmtDate(o.created_at)}</div>
                    <div className="font-serif text-2xl text-brand-ink leading-tight">{o.recipient_name}</div>
                    <div className="text-brand-ink2 text-sm italic font-serif">{o.occasion}</div>
                  </div>
                  <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] tracking-widest uppercase ${statusTone(o.status)}`}>{o.status}</span>
                </div>
                <div className="text-sm text-brand-ink2 leading-relaxed mb-4">
                  <span className="capitalize text-brand-ink font-medium">{(o.tree_key || '').replace(/^\w/, (c) => c.toUpperCase())}</span> tree ·
                  <span className="capitalize ml-1">{o.plan_key}</span> plan ·
                  <span className="ml-1">{formatINR(Math.round((o.amount_paise || 0) / 100))}</span>
                </div>
                <a
                  href={`${API}/orders/${o.order_id}/certificate.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`garden-download-${o.order_id}`}
                  className="inline-flex items-center gap-2 rounded-full border border-brand-forest text-brand-forest hover:bg-brand-forest hover:text-brand-bg px-4 py-2 text-xs tracking-wide font-medium transition-all"
                >
                  <Download className="w-3.5 h-3.5" /> Certificate PDF
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
